import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import Fab from '../Fab';
import Pagination, { getPageSize } from '../Pagination';
import { RC } from '../../utils/fonts';
import { fmtDateTime, fmtNumber, currencyCode } from '../../utils/region';
import { brandOr } from '../../utils/brand';
import { normalisePersonNamePart } from '../../utils/personName';
import { COUNTRIES } from '../../utils/countries';
import { propCard, PROP_CARD_BG, PROP_CARD_HOVER } from '../../utils/propertyCard';   // the console's green-and-white bubble, shared with Due Diligence / POA / Legal Matters / Last Will

// ── Client Property Form — SLF Property's sixth tab (report 1949e35a) ──────────────────────────
// One form per property (PROP-00001): the questions (POA / Last Will / agreements), up to three
// buyers, up to three sellers, the property and — only when agreements are being prepared — the
// agreement. Staff fill it in here; the client fills it in through the EDIT link or reads it through
// the VIEW link (PropertyFormPublicPage below, served by routes/propertyFormPublic.ts) — the same
// form, the same autosave, the same document readings.
//
// A RECORD ONLY for now (the reporter's call): nothing here copies into Due Diligence, POA or Last
// Will, starts an agreement, prints or emails. More fields will follow as the reporter sends them.
//
// A document given to a block (an ID card / passport on a buyer or individual seller, the company
// certificate / affidavit on a company seller, the title deed, the house book, an agreement) is kept with
// the form and read by AI; what it says fills that block's BLANKS only — an answer somebody typed is never
// overwritten by a file. A second agreement is added to the first one's figures only when the operator
// says Yes to combining the totals. Removing the document takes those boxes back: each box that kind of
// document fills goes blank again, ready for the right one; boxes no document carries (an email, a
// buyer's parents) keep their answers.
//
// Every box is a box of its kind (report 2999c435, the reporter's standing rule for forms): names are
// name boxes, prices money boxes with the firm's currency code inside, numbers number boxes, emails
// email boxes, addresses Line 1 / Line 2 / City / Post Code / Country. Every box is mandatory unless
// it says it is optional (Address Line 2, and a person's Middle name — report c39980c2): a red * on its
// label, a red outline while it is blank, and the form cannot be moved to Pending or Confirmed until each
// box it shows is answered.

const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 };
const lbl: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13.5, background: 'var(--bg-app)', color: 'var(--text-primary)', boxSizing: 'border-box' };
const roInp: React.CSSProperties = { background: 'var(--c-gray-200)', color: 'var(--text-secondary)', cursor: 'default' };
const btnG: React.CSSProperties = { padding: '8px 16px', fontSize: 13.5, fontWeight: 700, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer' };
const hdr: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginTop: 4 };
const rowBox: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'grid', gap: 10, background: 'var(--bg-app)' };
const errBox: React.CSSProperties = { fontSize: 12.5, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 6, padding: '6px 10px' };
const noteBox: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-secondary)', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px' };
// A required box: Due Diligence's red * on the label, and a red outline while it is blank. The outline
// replaces the whole `border`, never just its colour — taking a lone borderColor away again would
// leave the box with no border colour at all.
const star: React.CSSProperties = { color: '#D0021B', marginLeft: 3 };
const badInp: React.CSSProperties = { border: '1px solid #D0021B' };
const badHint: React.CSSProperties = { fontSize: 12, color: '#B42318', marginTop: 3 };
// An optional box (a person's Middle name, report c39980c2): no *, no red outline, and a grey
// "(optional)" after its label.
const optMark: React.CSSProperties = { fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: 4 };
// A box a document's reading filled with an answer the AI may have worked out rather than copied (the
// Post Code): an amber outline and an amber line asking for it to be checked. Amber, not red — it is answered.
const checkInp: React.CSSProperties = { border: '1px solid #D97706' };
const checkHint: React.CSSProperties = { fontSize: 12, color: '#B45309' };
// The currency code inside a price box's right end.
const curTag: React.CSSProperties = { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)', pointerEvents: 'none' };
const STATUSES = ['', 'new', 'pending', 'confirmed'];
const MAX_PEOPLE = 3;   // a second and a third buyer / seller (the reporter's "add a second / third … button")
const MAX_STAGES = 9;   // payment stages, the reporter's "up to 9"
// A payment stage's row — Stage | Percentage | Amount — and the totals line under the stages, column for column.
const STAGE_COLS = 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1.5fr)';

// Every dropdown's answers. The stored value is the English answer (what Due Diligence will read
// when the reporter turns that on); the label shown is the operator's language.
const TITLES = ['Mr.', 'Mrs.', 'Ms.'];
const SELLER_KINDS: [string, string][] = [['Individual', 'pf_individual'], ['Company', 'pf_company']];
const PROP_TYPES: [string, string][] = [['Land', 'pf_pt_land'], ['Building', 'pf_pt_building'], ['Land and Building', 'pf_pt_land_building'], ['Condominium', 'pf_pt_condo'], ['Apartment', 'pf_pt_apartment']];
const LAND_TYPES = ['Land', 'Land and Building'];
const BUILDING_TYPES = ['Building', 'Land and Building', 'Condominium', 'Apartment'];
const DEED_TYPES = ['Chanote', 'Nor Sor 3 Gor', 'Phor Tor Bor 5'];
const STAGES: [string, string][] = [
  ['Reservation Fee', 'pf_stage_reservation'], ['Deposit', 'pf_stage_deposit'], ['Upon Signing Contracts', 'pf_stage_signing'],
  ['Mobilization On-Site', 'pf_stage_mobilization'], ['Commencement On-Site', 'pf_stage_commencement'], ['Structure Complete', 'pf_stage_structure'],
  ['Watertight Building', 'pf_stage_watertight'], ['Lock-Up', 'pf_stage_lockup'], ['Land Transfer', 'pf_stage_land_transfer'],
  ['Building Transfer', 'pf_stage_building_transfer'], ['Land and Building Transfer', 'pf_stage_lb_transfer'],
  ['Land Lease Registration', 'pf_stage_land_lease'], ['Building Lease Registration', 'pf_stage_building_lease'],
  ['Land and Building Lease Registration', 'pf_stage_lb_lease'], ['DBD Transfer', 'pf_stage_dbd'],
  ['Shareholder Change', 'pf_stage_shareholder_change'], ['Director Change', 'pf_stage_director_change'], ['Handover', 'pf_stage_handover'],
];
const FEES_BY: [string, string][] = [['Buyer and seller share', 'pf_fees_share'], ['Seller pays all', 'pf_fees_seller'], ['Buyer pays all', 'pf_fees_buyer']];
// What a buyer or lessee is acquiring (report 21148c83): a tick box for each, kept on the block as `roles`
// in this order. One person can hold more than one (the reporter: a foreigner can lease the land and buy the
// building), but never Buyer and Lessee of the same thing — ROLE_OTHER is the tick a tick takes away.
const ROLES: [string, string][] = [
  ['Buyer of the land', 'pf_role_buy_land'], ['Buyer of the building', 'pf_role_buy_building'],
  ['Lessee of the land', 'pf_role_lease_land'], ['Lessee of the building', 'pf_role_lease_building'],
];
const ROLE_NAMES = ROLES.map(([v]) => v);
const ROLE_OTHER: Record<string, string> = {
  'Buyer of the land': 'Lessee of the land', 'Lessee of the land': 'Buyer of the land',
  'Buyer of the building': 'Lessee of the building', 'Lessee of the building': 'Buyer of the building',
};

// ── What a box keeps (report 2999c435) ──────────────────────────────────────────────────────────
// A Thai ID card number is 13 digits, written 1-4-5-2-1 ("1-2345-67890-12-3"). Once the ID / passport
// box holds exactly 13 digits — typed in one go, or the AI's spaced copy of the card — it gets those
// dashes; anything else (a passport number, a number half typed) stays as it is.
const fmtIdNo = (v: any): string => {
  const s = String(v ?? '');
  const d = s.replace(/[\s-]/g, '');
  return /^\d{13}$/.test(d) ? `${d[0]}-${d.slice(1, 5)}-${d.slice(5, 10)}-${d.slice(10, 12)}-${d[12]}` : s;
};
// A number box keeps digits only, and one decimal point unless it counts whole things (bedrooms).
const numText = (v: string, whole?: boolean): string => {
  const s = String(v ?? '').replace(whole ? /[^0-9]/g : /[^0-9.]/g, '');
  const i = s.indexOf('.');
  return i < 0 ? s : `${s.slice(0, i + 1)}${s.slice(i + 1).replace(/\./g, '')}`;
};
// A money box keeps the plain number ("12500000.00"): digits, one point, at most two decimals. The
// thousands separators are only how it is shown, so nothing that reads it later has commas to strip.
const moneyText = (v: string): string => {
  const s = numText(v);
  const i = s.indexOf('.');
  return i < 0 ? s : s.slice(0, i + 3);
};
// Two decimals once the box is left: "12500000" → "12500000.00". Padding only — never rounded.
const twoDp = (s: string): string => {
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(s);
  return m && /\d/.test(s) ? `${m[1].replace(/^0+(?=\d)/, '') || '0'}.${(m[2] || '').padEnd(2, '0')}` : s;
};
// How a money box shows its number: the whole part grouped in thousands (the region's way, as Due
// Diligence's amount boxes do), the decimals exactly as typed so a half-typed "1500." keeps its point.
const grouped = (s: string): string => {
  const m = /^(\d*)(\.\d*)?$/.exec(String(s ?? ''));
  return m ? `${m[1] ? fmtNumber(m[1]) : ''}${m[2] ?? ''}` : String(s ?? '');
};
// The number in what a document's reading gave ("120.5 m²" → "120.5", "3 bedrooms" → "3"); '' = none.
const leadNum = (v: string, whole?: boolean): string => {
  const m = (whole ? /\d+/ : /\d+(?:\.\d+)?/).exec(String(v ?? '').replace(/,/g, ''));
  return m ? m[0] : '';
};
// An answer saved before these boxes were typed: commas and spaces go when what is left is a plain
// number (money also gets its two decimals); anything else stays as typed, for a person to correct.
const numLoad = (v: any): string => { const s = String(v ?? '').replace(/[,\s]/g, ''); return /^\d*\.?\d*$/.test(s) ? s : String(v ?? ''); };
const moneyLoad = (v: any): string => { const s = numLoad(v); return /^\d*\.?\d*$/.test(s) ? twoDp(s) : s; };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmail = (v: any): boolean => EMAIL_RE.test(String(v ?? '').trim());
const blank = (v: any): boolean => !String(v ?? '').trim();

// ── Payment stages: Stage · Percentage · Amount (report e897c965) ─────────────────────────────────
// A money or % box's number as whole hundredths (cents / hundredths of a per cent), so a sum or a
// comparison is exact; null = blank or not a number.
const hundredths = (v: any): number | null => {
  const s = String(v ?? '').replace(/[,\s]/g, '');
  return /^(\d+\.?\d*|\.\d+)$/.test(s) ? Math.round(Number(s) * 100) : null;
};
const fromHundredths = (n: number): string => `${Math.floor(n / 100)}.${String(n % 100).padStart(2, '0')}`;
/** A typed % → its amount: price × % / 100, to the cent. '' while there is no price to take it of. */
const stageAmount = (price: any, pct: any): string => {
  const p = hundredths(price); const q = hundredths(pct);
  return p && q != null ? fromHundredths(Math.round(p * q / 10000)) : '';
};
/** A typed amount → its %: amount / price × 100, to two decimals. '' while there is no price. */
const stagePct = (price: any, amount: any): string => {
  const p = hundredths(price); const a = hundredths(amount);
  return p && a != null ? fromHundredths(Math.round(a * 10000 / p)) : '';
};
/** What the stages' amounts add up to, in cents. */
const stageSum = (stages: any[]): number => stages.reduce((n: number, s: any) => n + (hundredths(s?.amount) || 0), 0);
// A stage saved before the % box holds a stage and an amount only: its % is worked out from the amount
// and the price, as typing that amount would have done. Opening a form still saves nothing.
const stageLoad = (s: any, price: string): any => {
  const amount = moneyLoad(s.amount);
  const pct = moneyLoad(s.pct);
  return { ...s, pct: blank(pct) ? stagePct(price, amount) : pct, amount };
};

// An address in parts, the shape of Due Diligence's address box plus a second line. A form saved
// before the parts holds the address on one line: that line becomes Address Line 1, so nothing is lost.
const emptyAddress = () => ({ line1: '', line2: '', city: '', postal: '', country: '' });
const normAddress = (v: any): any => (v && typeof v === 'object' && !Array.isArray(v)
  ? { ...emptyAddress(), ...v }
  : { ...emptyAddress(), line1: String(v ?? '').trim() });
// The parts that must be answered — all but Line 2 — with their labels, for the list of missing boxes.
const ADDRESS_PARTS: [string, string][] = [['line1', 'pf_addr_line1'], ['city', 'pf_addr_city'], ['postal', 'pf_addr_postal'], ['country', 'pf_addr_country']];

// Each buyer / seller block carries a `uid`: the key its documents are filed under ('person:<uid>'),
// so removing buyer 2 never hands buyer 3's passport to somebody else. A buyer's parents are asked for
// only while a POA is being done; they stay on the buyer if the answer goes back to No.
const uid = (): string => Math.random().toString(36).slice(2, 12);
const emptyBuyer = () => ({ uid: uid(), roles: [] as string[], title: '', first: '', middle: '', last: '', email: '', idNo: '', nationality: '', address: emptyAddress(), fatherFirst: '', fatherLast: '', motherFirst: '', motherLast: '' });
// A buyer's ticks in the form's order, only the four it offers; a form saved before them has none ticked.
const rolesOf = (v: any): string[] => (Array.isArray(v) ? ROLE_NAMES.filter(r => v.indexOf(r) >= 0) : []);
const emptySeller = () => ({ uid: uid(), kind: 'Individual', propType: '', title: '', first: '', middle: '', last: '', email: '', idNo: '', nationality: '', address: emptyAddress(), companyName: '', taxId: '' });
const emptyProperty = () => ({ name: '', deedType: '', deedNo: '', landSqm: '', unitNo: '', houseBookNo: '', buildingSqm: '', bedrooms: '', bathrooms: '' });
const emptyAgreement = () => ({ price: '', stageCount: '', stages: [] as any[], feesPaidBy: '' });
const people = (v: any, make: () => any): any[] => {
  const l = Array.isArray(v) ? v.filter((x: any) => x && typeof x === 'object').slice(0, MAX_PEOPLE) : [];
  return (l.length ? l : [{}]).map((x: any) => {
    const p = { ...make(), ...x };
    return { ...p, idNo: fmtIdNo(p.idNo), address: normAddress(p.address) };
  });
};
const normForm = (d: any): any => {
  const prop = { ...emptyProperty(), ...(d?.property || {}) };
  const agr = { ...emptyAgreement(), ...(d?.agreement || {}) };
  const price = moneyLoad(agr.price);
  return {
    doPoa: '', doWill: '', doAgreements: '', ...(d || {}),
    buyers: people(d?.buyers, emptyBuyer).map((b: any) => ({ ...b, roles: rolesOf(b.roles) })),
    sellers: people(d?.sellers, emptySeller),
    property: { ...prop, landSqm: numLoad(prop.landSqm), buildingSqm: numLoad(prop.buildingSqm), bedrooms: numLoad(prop.bedrooms), bathrooms: numLoad(prop.bathrooms) },
    agreement: {
      ...agr, price,
      stages: Array.isArray(agr.stages) ? agr.stages.map((s: any) => (s && typeof s === 'object' ? stageLoad(s, price) : s)) : [],
    },
  };
};

// What each document reading may fill, and the dropdowns it may only fill with a listed answer. A
// person's and a company's reading also fill the address, part by part ('address.city' …).
const PERSON_KEYS = ['title', 'first', 'middle', 'last', 'idNo', 'nationality'];
// Both parents' names (report f6b1169a) — the boxes a buyer's block shows while a POA is being done,
// filled from a document that gives them (a house registration, a birth certificate, an ID back page).
const PARENT_KEYS = ['fatherFirst', 'fatherLast', 'motherFirst', 'motherLast'];
const COMPANY_KEYS = ['companyName', 'taxId'];
const ADDRESS_KEYS = ['line1', 'line2', 'city', 'postal', 'country'];
const DEED_KEYS = ['deedType', 'deedNo', 'landSqm'];
const BOOK_KEYS = ['unitNo', 'houseBookNo', 'buildingSqm', 'bedrooms', 'bathrooms'];
const ALLOWED: Record<string, string[]> = { title: TITLES, deedType: DEED_TYPES, country: COUNTRIES, nationality: COUNTRIES };
// A reading goes in the way typing would put it: the ID card number with its dashes, a name with its
// capitals, a number box only its number ('' = nothing usable, so the box stays blank).
const CLEAN: Record<string, (v: string) => string> = {
  idNo: fmtIdNo, first: v => normalisePersonNamePart(v), middle: v => normalisePersonNamePart(v), last: v => normalisePersonNamePart(v),
  fatherFirst: v => normalisePersonNamePart(v), fatherLast: v => normalisePersonNamePart(v),
  motherFirst: v => normalisePersonNamePart(v), motherLast: v => normalisePersonNamePart(v),
  landSqm: v => leadNum(v), buildingSqm: v => leadNum(v), bedrooms: v => leadNum(v, true), bathrooms: v => leadNum(v, true),
};
/** BLANKS ONLY: a box somebody already answered keeps its answer. Returns the block and how many boxes it filled. */
const fillBlanks = (obj: any, fields: Record<string, string>, keys: string[], prefix = ''): { next: any; n: number } => {
  const next = { ...(obj || {}) }; let n = 0;
  for (const k of keys) {
    const raw = String(fields?.[prefix + k] ?? '').trim();
    const v = raw && CLEAN[k] ? CLEAN[k](raw) : raw;
    if (!v || String(next[k] ?? '').trim()) continue;
    if (ALLOWED[k] && ALLOWED[k].indexOf(v) < 0) continue;
    next[k] = v; n++;
  }
  return { next, n };
};
/** A person's or a company's reading: its own boxes, then its address's blank parts. `postal` = it filled
 *  the Post Code, which the AI works out from the address when the document prints none (a Thai ID card). */
const fillWithAddress = (row: any, fields: Record<string, string>, keys: string[]): { next: any; n: number; postal: boolean } => {
  const own = fillBlanks(row, fields, keys);
  const was = normAddress(row?.address);
  const addr = fillBlanks(was, fields, ADDRESS_KEYS, 'address.');
  return { next: { ...own.next, address: addr.next }, n: own.n + addr.n, postal: addr.next.postal !== was.postal };
};
/** A document removed from its block takes back what its kind of reading fills (report 1d398933): each of
 *  those boxes goes blank, whoever answered it, so the next document's reading can fill it again. */
const clearKeys = (obj: any, keys: string[]): any => { const next = { ...(obj || {}) }; for (const k of keys) next[k] = ''; return next; };
/** A person's or a company's: its own boxes and the whole address. */
const clearWithAddress = (row: any, keys: string[]): any => ({ ...clearKeys(row, keys), address: clearKeys(normAddress(row?.address), ADDRESS_KEYS) });

// ── An agreement's reading (report eaf3b006) ─────────────────────────────────────────────────────
// The purchase price, the payment stages in the agreement's order — a stage's name only when it is one of
// STAGES; its amount as printed or, for a stage the agreement gives only as a percentage, that share of the
// agreement's own price — and who pays the fees. `count` is how many stages the agreement has, which can be
// more than the MAX_STAGES the form holds.
type AgrRead = { price: string; count: number; stages: { stage: string; amount: string }[]; fees: string };
const STAGE_NAMES = STAGES.map(([v]) => v);
const FEES_NAMES = FEES_BY.map(([v]) => v);
const readMoney = (v: any): string => { const s = moneyText(leadNum(v)); return s ? twoDp(s) : ''; };
const agrRead = (f: Record<string, string>): AgrRead => {
  const price = readMoney(f?.price);
  const all = Array.from({ length: MAX_STAGES }, (_, i) => {
    const k = `stages.${i + 1}.`;
    const stage = String(f?.[k + 'stage'] ?? '').trim();
    const pct = moneyText(leadNum(f?.[k + 'pct']));
    return { stage: STAGE_NAMES.indexOf(stage) >= 0 ? stage : '', amount: readMoney(f?.[k + 'amount']) || (pct ? stageAmount(price, pct) : '') };
  });
  const last = all.reduce((n, s, i) => (s.stage || s.amount ? i + 1 : n), 0);
  const count = Math.max(last, parseInt(leadNum(f?.stageCount, true), 10) || 0);
  const fees = String(f?.feesPaidBy ?? '').trim();
  return { price, count, stages: all.slice(0, Math.min(count, MAX_STAGES)), fees: FEES_NAMES.indexOf(fees) >= 0 ? fees : '' };
};
/** The stage rows the form shows for a stage count: those kept, then empty ones up to it. */
const stageRows = (agr: any, n: number): any[] => {
  const l = (Array.isArray(agr?.stages) ? agr.stages : []).slice(0, n).map((s: any) => (s && typeof s === 'object' ? s : { stage: '', pct: '', amount: '' }));
  while (l.length < n) l.push({ stage: '', pct: '', amount: '' });
  return l;
};
/** An agreement read while no other one's price is in the form → the Agreement's BLANKS, as every reading
 *  fills. A price it fills works each stage's % out again from its amount, and an amount it fills works out
 *  that stage's %, as typing them does. `over` = the agreement's stages past the MAX_STAGES its count filled in. */
const fillAgreement = (agr: any, r: AgrRead): { next: any; n: number; over: number } => {
  const a = agr || {};
  const price = blank(a.price) && r.price ? r.price : a.price;
  const stageCount = blank(a.stageCount) && r.count ? String(Math.min(r.count, MAX_STAGES)) : a.stageCount;
  const feesPaidBy = blank(a.feesPaidBy) && r.fees ? r.fees : a.feesPaidBy;
  let n = Number(price !== a.price) + Number(stageCount !== a.stageCount) + Number(feesPaidBy !== a.feesPaidBy);
  const k = Math.max(0, Math.min(MAX_STAGES, parseInt(stageCount, 10) || 0));
  const stages = stageRows(a, k).map((s: any, i: number) => {
    const got = r.stages[i];
    const row = { ...s };
    if (got?.stage && blank(row.stage)) { row.stage = got.stage; n++; }
    if (got?.amount && blank(row.amount)) { row.amount = got.amount; n++; }
    if (hundredths(price) && hundredths(row.amount) != null && (price !== a.price || row.amount !== s.amount)) row.pct = stagePct(price, row.amount);
    return row;
  });
  const over = stageCount !== a.stageCount ? Math.max(0, r.count - MAX_STAGES) : 0;
  return { next: { ...a, price, stageCount, stages: k ? stages : a.stages, feesPaidBy }, n, over };
};
/** A further agreement added to the figures another one filled, once the operator says Yes (the reporter: a
 *  land lease beside a building sale): the two prices summed to the cent, its stages after the rows already
 *  there up to MAX_STAGES (`over` = those that did not fit), and every % worked out again from its amount
 *  against the combined price, as a new price does. A blank fees box takes its answer. */
const addAgreement = (agr: any, r: AgrRead): { next: any; added: number; over: number } => {
  const a = agr || {};
  const price = fromHundredths((hundredths(a.price) || 0) + (hundredths(r.price) || 0));
  const k = Math.max(0, Math.min(MAX_STAGES, parseInt(a.stageCount, 10) || 0));
  const added = Math.min(r.stages.length, MAX_STAGES - k);
  const stages = stageRows(a, k).concat(r.stages.slice(0, added).map(s => ({ stage: s.stage, pct: '', amount: s.amount })))
    .map((s: any) => (hundredths(s.amount) != null ? { ...s, pct: stagePct(price, s.amount) } : s));
  const feesPaidBy = blank(a.feesPaidBy) && r.fees ? r.fees : a.feesPaidBy;
  return { next: { ...a, price, stageCount: k + added ? String(k + added) : a.stageCount, stages, feesPaidBy }, added, over: r.count - added };
};
/** A further agreement the operator chose not to add: the figures stay; only a blank fees box takes its answer. */
const feesOnly = (agr: any, r: AgrRead): { next: any; n: number } =>
  (blank(agr?.feesPaidBy) && r.fees ? { next: { ...agr, feesPaidBy: r.fees }, n: 1 } : { next: agr, n: 0 });
/** The name Contacts is asked about for a block's Email (report 33b5ec4e): a company seller's Company name,
 *  anybody else's First and Last name once both are filled in, with the Middle name between them when there
 *  is one (report c39980c2). '' = nothing to ask about yet. */
const contactName = (key: 'buyers' | 'sellers', r: any): string => (key === 'sellers' && r?.kind === 'Company'
  ? String(r.companyName ?? '').trim()
  : !blank(r?.first) && !blank(r?.last) ? [r.first, r.middle, r.last].map((x: any) => String(x ?? '').trim()).filter(Boolean).join(' ') : '');

// ── The boxes still to answer before a form can be Pending or Confirmed ───────────────────────────
// Grouped by block, in the order the form shows them. Every box the form is SHOWING is required except
// Address Line 2 and a person's Middle name; a box it is not showing is not (a company seller's personal
// boxes, the land boxes with no land, the agreement when none is being prepared, a buyer's parents with
// no POA). Payment stages that do not add up to the purchase price are listed with them (report
// e897c965). Mirror of missingBoxes in server/src/services/propertyForm.ts, which refuses the same move —
// keep them in step.
type Gap = { block: string; boxes: string[] };
function missingBoxes(d: any, t: TFunction): Gap[] {
  const out: Gap[] = [];
  let l: string[] = [];
  const need = (v: any, label: string) => { if (blank(v)) l.push(label); };
  const close = (block: string) => { if (l.length) out.push({ block, boxes: l }); l = []; };
  const email = (v: any) => { if (!isEmail(v)) l.push(t('pf_email')); };
  const address = (v: any, label: string) => {
    const a = normAddress(v);
    const parts = ADDRESS_PARTS.filter(([k]) => blank(a[k])).map(([, key]) => t(key));
    if (parts.length) l.push(`${label} (${parts.join(', ')})`);
  };
  const person = (p: any, parents: boolean) => {
    need(p.title, t('pf_title')); need(p.first, t('pf_first')); need(p.last, t('pf_last'));
    email(p.email); need(p.idNo, t('pf_id_no')); need(p.nationality, t('pf_nationality')); address(p.address, t('pf_address'));
    if (parents) {
      need(p.fatherFirst, t('pf_father_first')); need(p.fatherLast, t('pf_father_last'));
      need(p.motherFirst, t('pf_mother_first')); need(p.motherLast, t('pf_mother_last'));
    }
  };
  need(d.doPoa, t('pf_q_poa')); need(d.doWill, t('pf_q_will')); need(d.doAgreements, t('pf_q_agreements'));
  close(t('pf_sec_questions'));
  (d.buyers || []).forEach((b: any, i: number) => {
    if (!rolesOf(b.roles).length) l.push(t('pf_roles'));
    person(b, d.doPoa === 'Yes'); close(t('pf_buyer_n', { n: i + 1 }));
  });
  (d.sellers || []).forEach((s: any, i: number) => {
    need(s.kind, t('pf_seller_type')); need(s.propType, t('pf_prop_type'));
    if (s.kind === 'Company') {
      need(s.companyName, t('pf_company_name')); email(s.email); need(s.taxId, t('pf_tax_id')); address(s.address, t('pf_reg_address'));
    } else person(s, false);
    close(t('pf_seller_n', { n: i + 1 }));
  });
  const p = d.property || {};
  need(p.name, t('pf_prop_name'));
  close(t('pf_sec_property'));
  const types: string[] = (d.sellers || []).map((s: any) => String(s.propType || ''));
  if (types.some(x => LAND_TYPES.indexOf(x) >= 0)) {
    need(p.deedType, t('pf_deed_type')); need(p.deedNo, t('pf_deed_no')); need(p.landSqm, t('pf_land_sqm'));
    close(t('pf_land'));
  }
  if (types.some(x => BUILDING_TYPES.indexOf(x) >= 0)) {
    need(p.unitNo, t('pf_unit_no')); need(p.houseBookNo, t('pf_house_book_no'));
    need(p.buildingSqm, t('pf_building_sqm')); need(p.bedrooms, t('pf_bedrooms')); need(p.bathrooms, t('pf_bathrooms'));
    close(t('pf_building'));
  }
  if (d.doAgreements === 'Yes') {
    const a = d.agreement || {};
    need(a.price, t('pf_price')); need(a.stageCount, t('pf_stage_count'));
    const n = Math.max(0, Math.min(MAX_STAGES, parseInt(a.stageCount, 10) || 0));
    const stages: any[] = (Array.isArray(a.stages) ? a.stages : []).slice(0, n);
    for (let i = 0; i < n; i++) {
      need(stages[i]?.stage, t('pf_stage_n', { n: i + 1 }));
      need(stages[i]?.pct, `${t('pf_stage_n', { n: i + 1 })} (${t('pf_pct')})`);
      need(stages[i]?.amount, `${t('pf_stage_n', { n: i + 1 })} (${t('pf_amount')})`);
    }
    // The stages must come to 100% of the purchase price, to the cent (report e897c965). A blank price is
    // listed above already; a price that is not a number, or is nothing, can never be matched.
    const price = hundredths(a.price);
    if (n && !blank(a.price) && !(price && stageSum(stages) === price)) l.push(t('pf_stages_total_bad'));
    need(a.feesPaidBy, t('pf_fees_by'));
    close(t('pf_sec_agreement'));
  }
  return out;
}

const copyText = async (s: string): Promise<boolean> => { try { await navigator.clipboard.writeText(s); return true; } catch { return false; } };

// The view link shows the form exactly as the edit link does, every box read-only — and without the
// red marks, as nothing can be typed there.
const RO = createContext(false);

// A box's label; a required box's carries the red *, an optional box's a grey "(optional)" — neither on
// the view link, where nothing can be typed.
function Lbl({ text, req }: { text: string; req: boolean }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  return <span style={lbl}>{text}{!ro && (req ? <span style={star}>*</span> : <span style={optMark}>{t('pf_optional')}</span>)}</span>;
}
// One text box. Required unless `opt`; red while blank, or while `bad` says its answer is wrong; amber
// while `check` asks for an answer the form filled in to be checked.
// `input` carries the box's kind (inputMode, autoComplete, autoCapitalize, onBlur …); `tag` is
// written inside the box's right end (a price box's currency code).
function Txt({ label, value, onChange, ph, type, opt, bad, check, input, tag }: {
  label: string; value: string; onChange: (v: string) => void; ph?: string; type?: string; opt?: boolean; bad?: boolean; check?: boolean;
  input?: React.InputHTMLAttributes<HTMLInputElement>; tag?: string;
}) {
  const ro = useContext(RO);
  const red = !ro && !opt && (bad ?? blank(value));
  return (
    <div><Lbl text={label} req={!opt} />
      <div style={{ position: 'relative' }}>
        <input {...input} style={{ ...inp, ...(tag ? { paddingRight: 50 } : {}), ...(ro ? roInp : {}), ...(red ? badInp : {}), ...(check ? checkInp : {}) }} type={type || 'text'}
          placeholder={ro ? '' : ph} value={value || ''} readOnly={ro} onChange={e => onChange(e.target.value)} />
        {tag && <span style={curTag}>{tag}</span>}
      </div>
    </div>
  );
}
function Pick({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  const ro = useContext(RO);
  return (
    <div><Lbl text={label} req />
      <select style={{ ...inp, ...(ro ? roInp : {}), ...(!ro && !value ? badInp : {}) }} disabled={ro} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
// A name box: capitals as the client's keyboard types a name, the browser's own name suggestions, and
// the house capitals rule (utils/personName.ts — "SMITH" → "Smith") as the box is left. Never on the
// view link: a tidied answer there would be a save that link may not make. `opt` = it may stay blank
// (a Middle name).
function NameBox({ label, value, onChange, part, opt }: {
  label: string; value: string; onChange: (v: string) => void; part: 'given-name' | 'additional-name' | 'family-name'; opt?: boolean;
}) {
  const ro = useContext(RO);
  return <Txt label={label} value={value} onChange={onChange} opt={opt} input={{
    autoComplete: part, autoCapitalize: 'words',
    onBlur: () => { if (ro) return; const v = normalisePersonNamePart(value || ''); if (v !== (value || '')) onChange(v); },
  }} />;
}
// An email box: type=email, and an answer that is not an email address is marked once the box is left.
// `check` = the answer was filled in from Contacts: the box is amber with a line asking for it to be
// checked, as a Post Code a document's reading filled is, until somebody changes it (`onChecked`).
function EmailBox({ label, value, onChange, check, onChecked }: { label: string; value: string; onChange: (v: string) => void; check?: boolean; onChecked?: () => void }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const [typing, setTyping] = useState(false);
  const wrong = !blank(value) && !isEmail(value) && !typing;
  const checking = !!check && !ro && !blank(value);
  return (
    <div>
      <Txt label={label} type="email" value={value} onChange={v => { if (check && onChecked) onChecked(); onChange(v); }} bad={blank(value) || wrong} check={checking}
        input={{ autoComplete: 'email', onFocus: () => setTyping(true), onBlur: () => setTyping(false) }} />
      {wrong && !ro && <div style={badHint}>{t('pf_email_bad')}</div>}
      {checking && <div style={{ ...checkHint, marginTop: 3 }}><i className="ti ti-address-book" aria-hidden style={{ marginRight: 4 }} />{t('pf_email_check')}</div>}
    </div>
  );
}
// A number box: digits only (and a decimal point unless it counts whole things), a number keypad on a phone.
function NumBox({ label, value, onChange, whole }: { label: string; value: string; onChange: (v: string) => void; whole?: boolean }) {
  return <Txt label={label} value={value} onChange={v => onChange(numText(v, whole))} input={{ inputMode: whole ? 'numeric' : 'decimal' }} />;
}
// A price box: grouped in thousands as it is typed, two decimals once it is left (not on the view
// link), the firm's currency code (its region settings) inside the right end. What is kept is the
// plain number.
function MoneyBox({ label, value, onChange, cur }: { label: string; value: string; onChange: (v: string) => void; cur: string }) {
  const ro = useContext(RO);
  return <Txt label={label} value={grouped(value)} onChange={v => onChange(moneyText(v))} tag={cur} input={{
    inputMode: 'decimal',
    onBlur: () => { if (ro) return; const v = twoDp(value || ''); if (v !== (value || '')) onChange(v); },
  }} />;
}
// A percentage box (a payment stage's, report e897c965): it keeps its number as a price box does —
// digits, one point, two decimals once it is left (not on the view link) — with '%' inside the right end.
function PctBox({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const ro = useContext(RO);
  return <Txt label={label} value={value} onChange={v => onChange(moneyText(v))} tag="%" input={{
    inputMode: 'decimal',
    onBlur: () => { if (ro) return; const v = twoDp(value || ''); if (v !== (value || '')) onChange(v); },
  }} />;
}
// The totals line under the payment stages (report e897c965): the amounts' share of the purchase price
// and their sum, each under its column. Green when that is 100% and exactly the price; red with the
// difference otherwise — and until it matches the form cannot be moved to Pending or Confirmed
// (missingBoxes). The % is worked out from the summed amounts, so amounts that come to the price to the
// cent read 100.00% however each stage's own % was rounded. The view link shows it without the colours.
function StageTotals({ price, stages, cur }: { price: string; stages: any[]; cur: string }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const p = hundredths(price);
  const sum = stageSum(stages);
  const diff = p ? sum - p : 0;
  const ok = !!p && !diff;
  const share = (cents: number): string => (p ? grouped(fromHundredths(Math.round(Math.abs(cents) * 10000 / p))) : '—');
  const withCur = (cents: number): string => [grouped(fromHundredths(Math.abs(cents))), cur].filter(Boolean).join(' ');
  const color = ro ? 'var(--text-secondary)' : ok ? '#16a34a' : '#B42318';
  const msg = ro ? '' : !p ? t('pf_total_no_price') : ok ? t('pf_total_ok')
    : t(diff > 0 ? 'pf_total_over' : 'pf_total_short', { amount: withCur(diff), pct: `${share(diff)}%` });
  // Number at the left and unit at the right end, where the boxes above have theirs.
  const cell = (n: string, unit: string) => (
    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 6, padding: '0 10px 0 11px', minWidth: 0 }}>
      <span>{n}</span><span style={{ fontSize: 12.5 }}>{unit}</span>
    </span>
  );
  return (
    <div style={{ display: 'grid', gap: 4, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: STAGE_COLS, gap: 10, fontSize: 13.5, fontWeight: 700, color }}>
        <span>{t('pf_stages_total')}</span>
        {cell(share(sum), p ? '%' : '')}
        {cell(grouped(fromHundredths(sum)), cur)}
      </div>
      {msg && (
        <div style={{ fontSize: 12.5, color }}>
          <i className={`ti ${ok ? 'ti-circle-check' : 'ti-alert-circle'}`} aria-hidden style={{ marginRight: 4 }} />{msg}
        </div>
      )}
    </div>
  );
}
// An address in parts: Line 1, Line 2 (optional), City, Post Code and a Country dropdown. `check` = a
// document's reading filled the Post Code: that box is amber with a line asking for it to be checked,
// until somebody changes it (`onChecked`).
function AddressBox({ label, value, onChange, check, onChecked }: { label: string; value: any; onChange: (v: any) => void; check?: boolean; onChecked?: () => void }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const a = normAddress(value);
  const checking = !!check && !ro && !blank(a.postal);
  const part = (k: string, ph: string, ac: string, opt?: boolean) => (
    <input style={{ ...inp, ...(ro ? roInp : {}), ...(!ro && !opt && blank(a[k]) ? badInp : {}), ...(k === 'postal' && checking ? checkInp : {}) }} placeholder={ro ? '' : ph} autoComplete={ac}
      value={a[k] || ''} readOnly={ro} onChange={e => { if (k === 'postal' && check && onChecked) onChecked(); onChange({ ...a, [k]: e.target.value }); }} />
  );
  return (
    <div><Lbl text={label} req />
      <div style={{ display: 'grid', gap: 8 }}>
        {part('line1', t('pf_addr_line1'), 'address-line1')}
        {part('line2', t('pf_addr_line2'), 'address-line2', true)}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.35fr)', gap: 8 }}>
          {part('city', t('pf_addr_city'), 'address-level2')}
          {part('postal', t('pf_addr_postal'), 'postal-code')}
          <select style={{ ...inp, ...(ro ? roInp : {}), ...(!ro && blank(a.country) ? badInp : {}) }} disabled={ro} autoComplete="country-name"
            value={a.country || ''} onChange={e => onChange({ ...a, country: e.target.value })}>
            <option value="">{t('pf_addr_country')}</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {checking && <div style={{ ...checkHint, gridColumn: '2 / -1' }}><i className="ti ti-sparkles" aria-hidden style={{ marginRight: 4 }} />{t('pf_postal_check')}</div>}
        </div>
      </div>
    </div>
  );
}
// A buyer's or lessee's tick boxes (report 21148c83): land on the left, building on the right, Buyer above
// Lessee. As many as apply, at least one — red while none is ticked. Ticking Buyer of a thing takes the tick
// off Lessee of the same thing, and the other way round. Under Buyer of the land, the reporter's reminder
// that a foreigner can never own land directly: a hint only, shown whatever the buyer's Nationality says.
function RolesBox({ value, onChange }: { value: any; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const on = rolesOf(value);
  const flip = (r: string) => {
    const next = on.indexOf(r) >= 0 ? on.filter(x => x !== r) : on.filter(x => x !== ROLE_OTHER[r]).concat([r]);
    onChange(ROLE_NAMES.filter(x => next.indexOf(x) >= 0));
  };
  return (
    <div><Lbl text={t('pf_roles')} req />
      <div style={{ ...inp, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8, ...(ro ? roInp : {}), ...(!ro && !on.length ? badInp : {}) }}>
        {ROLES.map(([r, key]) => (
          <div key={r} style={{ minWidth: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: ro ? 'default' : 'pointer' }}>
              <input type="checkbox" checked={on.indexOf(r) >= 0} disabled={ro} onChange={() => flip(r)} style={{ width: 15, height: 15, margin: 0, flexShrink: 0 }} />
              {t(key)}
            </label>
            {r === 'Buyer of the land' && !ro && (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3, paddingLeft: 22 }}>
                <i className="ti ti-info-circle" aria-hidden style={{ marginRight: 4 }} />{t('pf_role_land_foreigner')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// What the form body needs from whoever shows it — the staff screen (by form id, logged in) or the
// client's link (by token, no login).
interface Ctl {
  data: any;
  set: (fn: (d: any) => any) => void;
  files: any[];
  upload: (file: File, path: string, kind: string) => Promise<any>;
  remove: (fileId: string) => Promise<any>;
  open: (f: any) => void;
  // Staff only: the one email Contacts holds for a company or person name ('' = none, or more than one).
  // The client's links have none, so nothing from Contacts ever reaches a client.
  contactEmail?: (name: string) => Promise<string>;
}

// The paste box beside a block's upload (report f3d358ea): focus it and paste a screenshot or a copied
// file, or drag a file onto it. What it gets goes to `onFile` — the copied file, else the clipboard's first
// image — and nothing else on the page sees the paste (an open chat would take a screenshot too). It is
// drawn only while the block can take a file, so its focus and drag state start afresh each time.
function PasteBox({ onFile }: { onFile: (file?: File | null) => void }) {
  const { t } = useTranslation();
  const [focus, setFocus] = useState(false);
  const [over, setOver] = useState(false);
  const hot = focus || over;
  return (
    <div tabIndex={0} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      onPaste={e => {
        e.preventDefault(); e.stopPropagation();
        const shot = Array.from(e.clipboardData?.items || []).find(it => /^image\//.test(it.type))?.getAsFile();
        onFile(e.clipboardData?.files?.[0] || shot);
      }}
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!over) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); e.stopPropagation(); setOver(false); onFile(e.dataTransfer?.files?.[0]); }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12.5, borderRadius: 8, outline: 'none', cursor: 'default',
        border: `1px dashed ${hot ? 'var(--accent)' : 'var(--border-strong)'}`, color: hot ? 'var(--accent)' : 'var(--text-tertiary)', background: over ? 'var(--bg-hover)' : 'transparent' }}>
      <i className="ti ti-clipboard" aria-hidden style={{ pointerEvents: 'none' }} />{t('pf_paste_here')}
    </div>
  );
}

// What a reading did, for the line under its upload: how many boxes it filled and whether one was the Post
// Code, or a line of its own (`msg`). A reading may first need the operator's Yes or No (`ask` — a second
// agreement: combine the totals?); the answer then does the filling and says what it did.
type Read = { n: number; postal?: boolean; msg?: string; ask?: Ask };
type Ask = { text: string; yes: string; no: string; answer: (yes: boolean) => Read };

// One block's documents: the upload (read by AI), the paste box beside it (PasteBox, the same way in) and
// the files already given to it. `onRead` says how many boxes the reading filled, and whether one was the
// Post Code — that one is asked to be checked.
// A question the reading asks stays on the page, under the upload, until it is answered: a dialog could
// be closed unseen while the operator is in another tab, and nothing more is uploaded here meanwhile.
// `onRemoved` runs once a file is removed: the block clears the boxes that kind of document fills.
function DocBox({ label, path, kind, ctl, onRead, onRemoved }: { label: string; path: string; kind: string; ctl: Ctl; onRead: (fields: Record<string, string>, file: any) => Read; onRemoved: () => void }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const [st, setSt] = useState<{ busy: boolean; msg: string; bad: boolean }>({ busy: false, msg: '', bad: false });
  const [ask, setAsk] = useState<Ask | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const mine = ctl.files.filter((f: any) => f.path === path);
  const wait = st.busy || !!ask;
  const said = (got: Read): string => got.msg ?? (got.n ? [t('pf_ai_filled', { n: got.n }), got.postal ? t('pf_ai_postal') : ''].filter(Boolean).join(' ') : t('pf_ai_nothing'));
  const pick = async (file?: File | null) => {
    if (!file) return;
    setSt({ busy: true, msg: t('pf_reading'), bad: false });
    try {
      const r: any = await ctl.upload(file, path, kind);
      if (r?.aiError) setSt({ busy: false, msg: t('pf_ai_failed', { msg: r.aiError }), bad: true });
      else {
        const got: Read = r?.fields ? onRead(r.fields, r.file) : { n: 0 };
        setAsk(got.ask || null);
        setSt({ busy: false, msg: got.ask ? '' : said(got), bad: false });
      }
    } catch (e: any) { setSt({ busy: false, msg: e?.message || '', bad: true }); }
    if (input.current) input.current.value = '';
  };
  // A file pasted or dropped on the paste box goes the upload's way. Only an image or a PDF, as the upload
  // offers; a screenshot that comes without a name of its own is called 'pasted image' (the upload needs one).
  const take = (file?: File | null) => {
    if (!file || !(/^image\//.test(file.type) || file.type === 'application/pdf')) { setSt({ busy: false, msg: t('pf_paste_bad'), bad: true }); return; }
    pick(file.name ? file : new File([file], `${t('pf_pasted')}.${file.type.split('/')[1].split('+')[0]}`, { type: file.type }));
  };
  const reply = (yes: boolean) => {
    if (!ask) return;
    const got = ask.answer(yes);
    setAsk(null);
    setSt({ busy: false, msg: said(got), bad: false });
  };
  // Only once the file is really gone: the block clears its boxes, and the last reading's line goes with
  // them ("Filled 7 boxes…" would no longer be true) unless a new document is being read right now. A
  // question still waiting goes too: the figures it asked about have just been cleared.
  const drop = async (fileId: string) => {
    if (!window.confirm(t('pf_remove_file_confirm'))) return;
    try { await ctl.remove(fileId); } catch { return; }
    setAsk(null);
    setSt(s => (s.busy ? s : { busy: false, msg: '', bad: false }));
    onRemoved();
  };
  if (ro && !mine.length) return null;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {!ro && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" disabled={wait} onClick={() => input.current?.click()} style={{ ...btnG, padding: '6px 12px', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: wait ? 'default' : 'pointer' }}>
            <i className={`ti ${st.busy ? 'ti-loader' : 'ti-sparkles'}`} aria-hidden />{label}
          </button>
          <input ref={input} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => pick(e.target.files?.[0])} />
          {!wait && <PasteBox onFile={take} />}
          {st.msg && <span style={{ fontSize: 12.5, color: st.bad ? '#B42318' : 'var(--text-tertiary)' }}>{st.msg}</span>}
        </div>
      )}
      {!ro && ask && (
        <div style={{ ...noteBox, display: 'grid', gap: 8, color: 'var(--text-primary)', border: '1px solid #D97706' }}>
          <span>{ask.text}</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => reply(true)} style={{ ...btnG, padding: '6px 12px', fontSize: 12.5, background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' }}>{ask.yes}</button>
            <button type="button" onClick={() => reply(false)} style={{ ...btnG, padding: '6px 12px', fontSize: 12.5 }}>{ask.no}</button>
          </div>
        </div>
      )}
      {mine.map((f: any) => (
        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, minWidth: 0 }}>
          <i className="ti ti-file-text" aria-hidden style={{ color: 'var(--text-tertiary)' }} />
          <button type="button" onClick={() => ctl.open(f)} style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--accent)', cursor: 'pointer', fontSize: 12.5, textAlign: 'left', overflowWrap: 'anywhere' }}>{f.filename}</button>
          <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{[f.created_by_name, f.created_at ? fmtDateTime(f.created_at) : ''].filter(Boolean).join(' · ')}</span>
          {!ro && (
            <button type="button" title={t('pf_remove')} onClick={() => drop(f.id)}
              style={{ border: 'none', background: 'transparent', padding: 0, color: '#b91c1c', cursor: 'pointer', fontSize: 14 }}>
              <i className="ti ti-trash" aria-hidden />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── The form itself: Questions · Buyer or Lessee · Seller · Property · Agreement ────────────────
// `cur` is the firm's currency code (its region settings), shown inside the price boxes.
function FormBody({ ctl, cur }: { ctl: Ctl; cur: string }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const { data, set } = ctl;
  // The answers on screen right now — what a document's reading is counted against before it is
  // applied (the update itself re-checks the blanks on the latest answers).
  const live = useRef<any>(data);
  live.current = data;
  // The documents on the form right now: an agreement's reading looks for another agreement already there.
  const liveFiles = useRef<any[]>(ctl.files);
  liveFiles.current = ctl.files;

  const setTop = (patch: any) => set((d: any) => ({ ...d, ...patch }));
  const setRow = (key: 'buyers' | 'sellers', i: number, patch: any) => set((d: any) => {
    const l = d[key].slice(); l[i] = { ...l[i], ...patch }; return { ...d, [key]: l };
  });
  const addRow = (key: 'buyers' | 'sellers', make: () => any) => set((d: any) => (d[key].length >= MAX_PEOPLE ? d : { ...d, [key]: d[key].concat([make()]) }));
  const removeRow = (key: 'buyers' | 'sellers', i: number) => set((d: any) => ({ ...d, [key]: d[key].filter((_: any, j: number) => j !== i) }));
  const setProp = (patch: any) => set((d: any) => ({ ...d, property: { ...d.property, ...patch } }));
  const setAgr = (patch: any) => set((d: any) => ({ ...d, agreement: { ...d.agreement, ...patch } }));
  // A new purchase price works each stage's % out again from its amount — the amounts are what is paid
  // (report e897c965). A stage with no amount, or no price to take a share of, keeps its % as it is.
  const setPrice = (v: string) => set((d: any) => {
    const l = Array.isArray(d.agreement.stages) ? d.agreement.stages : [];
    const stages = hundredths(v)
      ? l.map((s: any) => (s && typeof s === 'object' && hundredths(s.amount) != null ? { ...s, pct: stagePct(v, s.amount) } : s))
      : l;
    return { ...d, agreement: { ...d.agreement, price: v, stages } };
  });
  // The number of stages decides how many stage rows there are; the rows beyond it go, so what is
  // kept is what the screen shows.
  const setStageCount = (v: string) => set((d: any) => {
    const n = Math.max(0, Math.min(MAX_STAGES, parseInt(v, 10) || 0));
    const l = (Array.isArray(d.agreement.stages) ? d.agreement.stages : []).slice(0, n);
    while (l.length < n) l.push({ stage: '', pct: '', amount: '' });
    return { ...d, agreement: { ...d.agreement, stageCount: v, stages: l } };
  });
  // A typed % fills the amount and a typed amount fills the % (report e897c965), each against the
  // purchase price; emptying one empties the other. With no price yet, the other box keeps what it has.
  const setStage = (i: number, patch: any) => set((d: any) => {
    const l = (Array.isArray(d.agreement.stages) ? d.agreement.stages : []).slice();
    while (l.length <= i) l.push({ stage: '', pct: '', amount: '' });
    const row = { ...l[i], ...patch };
    const price = d.agreement.price;
    if (hundredths(price)) {
      if ('pct' in patch) row.amount = stageAmount(price, patch.pct);
      else if ('amount' in patch) row.pct = stagePct(price, patch.amount);
    }
    l[i] = row;
    return { ...d, agreement: { ...d.agreement, stages: l } };
  });

  // The blocks (by uid) whose Post Code a document's reading filled. The AI works the post code out from
  // the address when the document prints none (a Thai ID card never does), so that box asks to be checked
  // until somebody changes it. Kept on this screen only — nothing about it is saved with the form.
  const [postalToCheck, setPostalToCheck] = useState<Record<string, boolean>>({});
  const postalChecked = (rowUid: string) => () => setPostalToCheck(m => ({ ...m, [rowUid]: false }));

  // The blocks (by uid) whose Email was filled in from Contacts (report 33b5ec4e). No document carries an
  // email, so once a block's Company name — or a person's First and Last name — is filled in while its
  // Email box is blank, Contacts is asked for that name; the one email it holds fills the box, which asks to
  // be checked until somebody changes it, as the Post Code does. Each block's name is asked about once
  // (`asked`), so an Email emptied by hand stays empty until the name changes. Opening a form asks too: the
  // filled box is an answer like any other and saves. The staff screen only — the client's links have no
  // `contactEmail`, so nothing from Contacts reaches a client.
  const [emailToCheck, setEmailToCheck] = useState<Record<string, boolean>>({});
  const emailChecked = (rowUid: string) => () => setEmailToCheck(m => ({ ...m, [rowUid]: false }));
  const asked = useRef<Record<string, string>>({});
  const lookup = ro ? undefined : ctl.contactEmail;
  const wanted = lookup
    ? (['buyers', 'sellers'] as const).flatMap(key => (data[key] || []).map((r: any) => ({ key, uid: String(r.uid), name: blank(r.email) ? contactName(key, r) : '' })))
      .filter(w => w.name && asked.current[w.uid] !== w.name)
    : [];
  const wantedKey = JSON.stringify(wanted.map(w => [w.uid, w.name]));
  useEffect(() => {
    if (!lookup || !wanted.length) return;
    const h = setTimeout(() => {
      for (const w of wanted) {
        asked.current[w.uid] = w.name;
        lookup(w.name).then(email => {
          // Only while the block still has that name and a blank Email (the update re-checks the blank).
          const cur = (live.current?.[w.key] || []).find((r: any) => r.uid === w.uid);
          if (!email || !cur || !blank(cur.email) || contactName(w.key, cur) !== w.name) return;
          set((d: any) => ({ ...d, [w.key]: d[w.key].map((r: any) => (r.uid === w.uid && blank(r.email) ? { ...r, email } : r)) }));
          setEmailToCheck(m => ({ ...m, [w.uid]: true }));
        }).catch(() => { /* no answer, no fill */ });
      }
    }, 600);
    return () => clearTimeout(h);
  }, [wantedKey]); // eslint-disable-line

  // A reading of one person's ID card / passport → that block's blanks (found by uid, not position).
  // A document naming both parents (a house registration, a birth certificate, an ID back page) fills
  // those two pairs of boxes as well (report f6b1169a) — but only where they are on screen: a buyer's
  // block while a POA is being done, which is the only place the form asks for them.
  const readPerson = (key: 'buyers' | 'sellers', rowUid: string) => (fields: Record<string, string>): { n: number; postal: boolean } => {
    const keys = key === 'buyers' && live.current?.doPoa === 'Yes' ? PERSON_KEYS.concat(PARENT_KEYS) : PERSON_KEYS;
    const cur = (live.current?.[key] || []).find((r: any) => r.uid === rowUid);
    const got = cur ? fillWithAddress(cur, fields, keys) : { n: 0, postal: false };
    if (got.n) set((d: any) => ({ ...d, [key]: d[key].map((r: any) => (r.uid === rowUid ? fillWithAddress(r, fields, keys).next : r)) }));
    if (got.postal) setPostalToCheck(m => ({ ...m, [rowUid]: true }));
    return { n: got.n, postal: got.postal };
  };
  // A reading of a company seller's company certificate / affidavit → that seller's blanks (by uid too).
  const readCompany = (rowUid: string) => (fields: Record<string, string>): { n: number; postal: boolean } => {
    const cur = (live.current?.sellers || []).find((r: any) => r.uid === rowUid);
    const got = cur ? fillWithAddress(cur, fields, COMPANY_KEYS) : { n: 0, postal: false };
    if (got.n) set((d: any) => ({ ...d, sellers: d.sellers.map((r: any) => (r.uid === rowUid ? fillWithAddress(r, fields, COMPANY_KEYS).next : r)) }));
    if (got.postal) setPostalToCheck(m => ({ ...m, [rowUid]: true }));
    return { n: got.n, postal: got.postal };
  };
  const readProperty = (keys: string[]) => (fields: Record<string, string>): { n: number } => {
    const n = fillBlanks(live.current?.property, fields, keys).n;
    if (n) set((d: any) => ({ ...d, property: fillBlanks(d.property, fields, keys).next }));
    return { n };
  };
  // A document removed from a block (report 1d398933) → the boxes that kind of document fills go blank,
  // found by uid as the reading found them, and the change saves as typing does. A Post Code that goes
  // with them no longer asks to be checked.
  const clearRow = (key: 'buyers' | 'sellers', rowUid: string, keys: string[]) => () => {
    set((d: any) => ({ ...d, [key]: d[key].map((r: any) => (r.uid === rowUid ? clearWithAddress(r, keys) : r)) }));
    setPostalToCheck(m => ({ ...m, [rowUid]: false }));
  };
  const clearProperty = (keys: string[]) => () => set((d: any) => ({ ...d, property: clearKeys(d.property, keys) }));
  // An agreement's reading (report eaf3b006) → the Agreement's blanks, as every reading fills. When another
  // agreement is already on the form and the purchase price holds a figure, it first asks whether to combine
  // the totals (the reporter: a land lease and a building sale): Yes adds this agreement's price and stages
  // to the figures there, No keeps them. Stages past the form's MAX_STAGES are reported, never kept.
  const money = (v: any): string => [grouped(String(v ?? '')), cur].filter(Boolean).join(' ');
  const readAgreement = (fields: Record<string, string>, file: any): Read => {
    const r = agrRead(fields);
    const over = (n: number): string => (n > 0 ? t('pf_agr_over', { n, max: MAX_STAGES }) : '');
    const a = live.current?.agreement || {};
    const other = liveFiles.current.some((f: any) => f.path === 'agreement' && f.id !== file?.id);
    if (!(other && hundredths(a.price) && hundredths(r.price))) {
      const got = fillAgreement(a, r);
      if (got.n) set((d: any) => ({ ...d, agreement: fillAgreement(d.agreement, r).next }));
      return { n: got.n, msg: got.over ? `${t('pf_ai_filled', { n: got.n })} ${over(got.over)}` : undefined };
    }
    return { n: 0, ask: {
      text: t('pf_agr_combine_q', { price: money(a.price), add: money(r.price) }),
      yes: t('pf_agr_combine_yes'), no: t('pf_agr_combine_no'),
      answer: (yes: boolean): Read => {
        if (yes) {
          const got = addAgreement(live.current?.agreement, r);
          set((d: any) => ({ ...d, agreement: addAgreement(d.agreement, r).next }));
          return { n: got.added, msg: [t('pf_agr_combined', { price: money(got.next.price), n: got.added }), over(got.over)].filter(Boolean).join(' ') };
        }
        const got = feesOnly(live.current?.agreement, r);
        if (got.n) set((d: any) => ({ ...d, agreement: feesOnly(d.agreement, r).next }));
        return { n: got.n, msg: [t('pf_agr_kept'), got.n ? t('pf_ai_filled', { n: got.n }) : ''].filter(Boolean).join(' ') };
      },
    } };
  };
  // Removing an agreement takes back every box an agreement's reading fills: the price, the stages and the fees.
  const clearAgreement = () => set((d: any) => ({ ...d, agreement: { ...d.agreement, ...emptyAgreement() } }));

  const opts = (l: [string, string][]) => l.map(([value, key]) => ({ value, label: t(key) }));
  const yesNo = [{ value: 'Yes', label: t('pf_yes') }, { value: 'No', label: t('pf_no') }];
  const titleOpts = TITLES.map(v => ({ value: v, label: v }));
  const deedOpts = DEED_TYPES.map(v => ({ value: v, label: v }));
  const countryOpts = COUNTRIES.map(v => ({ value: v, label: v }));
  const countOpts = Array.from({ length: MAX_STAGES }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));

  const types: string[] = (data.sellers || []).map((s: any) => String(s.propType || ''));
  const hasLand = types.some(x => LAND_TYPES.indexOf(x) >= 0);
  const hasBuilding = types.some(x => BUILDING_TYPES.indexOf(x) >= 0);
  const agr = data.agreement || {};
  const stageN = Math.max(0, Math.min(MAX_STAGES, parseInt(agr.stageCount, 10) || 0));
  const stages: any[] = Array.isArray(agr.stages) ? agr.stages : [];

  const rowHead = (label: string, onRemove?: () => void) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{label}</span>
      {!ro && onRemove && <button type="button" onClick={onRemove} style={{ ...btnG, padding: '3px 9px', fontSize: 12, color: '#b91c1c' }}>{t('pf_remove')}</button>}
    </div>
  );
  // Title · first · middle · last, email · ID / passport No. · nationality, address — a buyer's block and an
  // individual seller's. The Middle name is optional (report c39980c2): it has its own box, so a middle name no
  // longer has to go in First name. A buyer's block also asks for both parents' names while a POA is being done
  // (the POA needs them).
  const personFields = (key: 'buyers' | 'sellers', p: any, i: number) => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr', gap: 10 }}>
        <Pick label={t('pf_title')} value={p.title} onChange={v => setRow(key, i, { title: v })} options={titleOpts} />
        <NameBox label={t('pf_first')} part="given-name" value={p.first} onChange={v => setRow(key, i, { first: v })} />
        <NameBox label={t('pf_middle')} part="additional-name" opt value={p.middle} onChange={v => setRow(key, i, { middle: v })} />
        <NameBox label={t('pf_last')} part="family-name" value={p.last} onChange={v => setRow(key, i, { last: v })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <EmailBox label={t('pf_email')} value={p.email} onChange={v => setRow(key, i, { email: v })}
          check={!!emailToCheck[p.uid]} onChecked={emailChecked(p.uid)} />
        <Txt label={t('pf_id_no')} value={p.idNo} onChange={v => setRow(key, i, { idNo: fmtIdNo(v) })} />
        <Pick label={t('pf_nationality')} value={p.nationality} onChange={v => setRow(key, i, { nationality: v })} options={countryOpts} />
      </div>
      <AddressBox label={t('pf_address')} value={p.address} onChange={v => setRow(key, i, { address: v })}
        check={!!postalToCheck[p.uid]} onChecked={postalChecked(p.uid)} />
      {key === 'buyers' && data.doPoa === 'Yes' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <NameBox label={t('pf_father_first')} part="given-name" value={p.fatherFirst} onChange={v => setRow(key, i, { fatherFirst: v })} />
            <NameBox label={t('pf_father_last')} part="family-name" value={p.fatherLast} onChange={v => setRow(key, i, { fatherLast: v })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <NameBox label={t('pf_mother_first')} part="given-name" value={p.motherFirst} onChange={v => setRow(key, i, { motherFirst: v })} />
            <NameBox label={t('pf_mother_last')} part="family-name" value={p.motherLast} onChange={v => setRow(key, i, { motherLast: v })} />
          </div>
        </>
      )}
      <DocBox label={t('pf_upload_id')} path={`person:${p.uid}`} kind="person" ctl={ctl} onRead={readPerson(key, p.uid)}
        onRemoved={clearRow(key, p.uid, PERSON_KEYS)} />
    </>
  );

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Questions — recorded only; nothing is started from them yet */}
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <div style={hdr}>{t('pf_sec_questions')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          <Pick label={t('pf_q_poa')} value={data.doPoa} onChange={v => setTop({ doPoa: v })} options={yesNo} />
          <Pick label={t('pf_q_will')} value={data.doWill} onChange={v => setTop({ doWill: v })} options={yesNo} />
          <Pick label={t('pf_q_agreements')} value={data.doAgreements} onChange={v => setTop({ doAgreements: v })} options={yesNo} />
        </div>
      </div>

      {/* Buyer or lessee — up to three */}
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <div style={hdr}>{t('pf_sec_buyer')}</div>
        {data.buyers.map((b: any, i: number) => (
          <div key={b.uid} style={rowBox}>
            {rowHead(t('pf_buyer_n', { n: i + 1 }), data.buyers.length > 1 ? () => removeRow('buyers', i) : undefined)}
            <RolesBox value={b.roles} onChange={v => setRow('buyers', i, { roles: v })} />
            {personFields('buyers', b, i)}
          </div>
        ))}
        {!ro && data.buyers.length < MAX_PEOPLE && (
          <button type="button" onClick={() => addRow('buyers', emptyBuyer)} style={{ ...btnG, justifySelf: 'start' }}>
            + {data.buyers.length === 1 ? t('pf_add_buyer2') : t('pf_add_buyer3')}
          </button>
        )}
      </div>

      {/* Seller — up to three; an individual or a company, each with its own kind of property */}
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <div style={hdr}>{t('pf_sec_seller')}</div>
        {data.sellers.map((s: any, i: number) => (
          <div key={s.uid} style={rowBox}>
            {rowHead(t('pf_seller_n', { n: i + 1 }), data.sellers.length > 1 ? () => removeRow('sellers', i) : undefined)}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Pick label={t('pf_seller_type')} value={s.kind} onChange={v => setRow('sellers', i, { kind: v })} options={opts(SELLER_KINDS)} />
              <Pick label={t('pf_prop_type')} value={s.propType} onChange={v => setRow('sellers', i, { propType: v })} options={opts(PROP_TYPES)} />
            </div>
            {s.kind === 'Company' ? (
              <>
                {/* A company's name is kept as typed (its capitals are its own); the house capitals rule is for people */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Txt label={t('pf_company_name')} value={s.companyName} onChange={v => setRow('sellers', i, { companyName: v })}
                    input={{ autoComplete: 'organization', autoCapitalize: 'words' }} />
                  <EmailBox label={t('pf_email')} value={s.email} onChange={v => setRow('sellers', i, { email: v })}
                    check={!!emailToCheck[s.uid]} onChecked={emailChecked(s.uid)} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Txt label={t('pf_tax_id')} value={s.taxId} onChange={v => setRow('sellers', i, { taxId: v })} />
                </div>
                <AddressBox label={t('pf_reg_address')} value={s.address} onChange={v => setRow('sellers', i, { address: v })}
                  check={!!postalToCheck[s.uid]} onChecked={postalChecked(s.uid)} />
                {/* Filed under this seller's uid, as an ID card is, so it stays with the seller if the type changes */}
                <DocBox label={t('pf_upload_company')} path={`person:${s.uid}`} kind="company" ctl={ctl} onRead={readCompany(s.uid)}
                  onRemoved={clearRow('sellers', s.uid, COMPANY_KEYS)} />
              </>
            ) : personFields('sellers', s, i)}
          </div>
        ))}
        {!ro && data.sellers.length < MAX_PEOPLE && (
          <button type="button" onClick={() => addRow('sellers', emptySeller)} style={{ ...btnG, justifySelf: 'start' }}>
            + {data.sellers.length === 1 ? t('pf_add_seller2') : t('pf_add_seller3')}
          </button>
        )}
      </div>

      {/* Property — the land and building questions follow the sellers' property types */}
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <div style={hdr}>{t('pf_sec_property')}</div>
        <Txt label={t('pf_prop_name')} value={data.property.name} onChange={v => setProp({ name: v })} ph={t('pf_prop_name_ph')} />
        {hasLand && (
          <div style={rowBox}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('pf_land')}</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Pick label={t('pf_deed_type')} value={data.property.deedType} onChange={v => setProp({ deedType: v })} options={deedOpts} />
              <Txt label={t('pf_deed_no')} value={data.property.deedNo} onChange={v => setProp({ deedNo: v })} />
              <NumBox label={t('pf_land_sqm')} value={data.property.landSqm} onChange={v => setProp({ landSqm: v })} />
            </div>
            <DocBox label={t('pf_upload_deed')} path="titleDeed" kind="titleDeed" ctl={ctl} onRead={readProperty(DEED_KEYS)} onRemoved={clearProperty(DEED_KEYS)} />
          </div>
        )}
        {hasBuilding && (
          <div style={rowBox}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('pf_building')}</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Txt label={t('pf_unit_no')} value={data.property.unitNo} onChange={v => setProp({ unitNo: v })} />
              <Txt label={t('pf_house_book_no')} value={data.property.houseBookNo} onChange={v => setProp({ houseBookNo: v })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <NumBox label={t('pf_building_sqm')} value={data.property.buildingSqm} onChange={v => setProp({ buildingSqm: v })} />
              <NumBox label={t('pf_bedrooms')} whole value={data.property.bedrooms} onChange={v => setProp({ bedrooms: v })} />
              <NumBox label={t('pf_bathrooms')} whole value={data.property.bathrooms} onChange={v => setProp({ bathrooms: v })} />
            </div>
            <DocBox label={t('pf_upload_house_book')} path="houseBook" kind="houseBook" ctl={ctl} onRead={readProperty(BOOK_KEYS)} onRemoved={clearProperty(BOOK_KEYS)} />
          </div>
        )}
        {!hasLand && !hasBuilding && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pf_choose_type')}</div>}
      </div>

      {/* Agreement — only when agreements are being prepared */}
      {data.doAgreements === 'Yes' && (
        <div style={{ ...card, display: 'grid', gap: 12 }}>
          <div style={hdr}>{t('pf_sec_agreement')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <MoneyBox label={t('pf_price')} cur={cur} value={agr.price} onChange={setPrice} />
            <Pick label={t('pf_stage_count')} value={agr.stageCount} onChange={setStageCount} options={countOpts} />
          </div>
          {Array.from({ length: stageN }, (_, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: STAGE_COLS, gap: 10 }}>
              <Pick label={t('pf_stage_n', { n: i + 1 })} value={stages[i]?.stage || ''} onChange={v => setStage(i, { stage: v })} options={opts(STAGES)} />
              <PctBox label={t('pf_pct')} value={stages[i]?.pct || ''} onChange={v => setStage(i, { pct: v })} />
              <MoneyBox label={t('pf_amount')} cur={cur} value={stages[i]?.amount || ''} onChange={v => setStage(i, { amount: v })} />
            </div>
          ))}
          {stageN > 0 && <StageTotals price={agr.price} stages={stages.slice(0, stageN)} cur={cur} />}
          <Pick label={t('pf_fees_by')} value={agr.feesPaidBy} onChange={v => setAgr({ feesPaidBy: v })} options={opts(FEES_BY)} />
          <DocBox label={t('pf_upload_agreement')} path="agreement" kind="agreement" ctl={ctl} onRead={readAgreement} onRemoved={clearAgreement} />
        </div>
      )}
    </div>
  );
}

// Whole-object autosave with the revision this screen loaded (the will's rule): a save over somebody
// else's newer one is refused and says so. Nothing is sent until an answer actually changes, so just
// opening a form — or reading it through the view link — leaves it, and its audit trail, as it was
// (unless the staff screen fills a blank Email from Contacts — FormBody, report 33b5ec4e).
// Saves run one after another, each with the revision the last one returned. `flush` saves what is on
// screen now, without waiting for the pause — a status change waits for it, so the server checks the
// same answers the screen shows.
function useAutosave(saveFn: (data: any, baseRev: number) => Promise<any>) {
  const [data, setData] = useState<any>(null);
  const [save, setSave] = useState<'' | 'saving' | 'saved'>('');
  const [err, setErr] = useState('');
  const saved = useRef('');
  const rev = useRef(0);
  const chain = useRef<Promise<any>>(Promise.resolve());
  const latest = useRef<any>(null);
  latest.current = data;
  const init = (d: any, r: number) => { const n = normForm(d); saved.current = JSON.stringify(n); rev.current = r; setData(n); };
  // One save, queued behind those already running; answers that are already saved are not sent again.
  const push = (d: any): Promise<any> => {
    chain.current = chain.current.then(() => {
      const json = JSON.stringify(d);
      if (json === saved.current) return undefined;
      return saveFn(d, rev.current)
        .then((r: any) => { saved.current = json; rev.current = Number(r?.rev || 0); setSave('saved'); setErr(''); })
        .catch((e: any) => { setSave(''); setErr(e?.message || ''); });
    });
    return chain.current;
  };
  useEffect(() => {
    if (data == null) return;
    if (JSON.stringify(data) === saved.current) return;
    setSave('saving');
    const h = setTimeout(() => { push(data); }, 700);
    return () => clearTimeout(h);
  }, [data]); // eslint-disable-line
  const flush = (): Promise<any> => (latest.current == null ? chain.current : push(latest.current));
  const set = (fn: (d: any) => any) => setData((d: any) => (d == null ? d : fn(d)));
  return { data, set, init, save, err, setErr, flush };
}

// ── Staff: one form ──────────────────────────────────────────────────────────────────────────────
function PFForm({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation();
  const [meta, setMeta] = useState<any>(null);
  const [files, setFiles] = useState<any[]>([]);
  const [status, setStatus] = useState('new');
  const [note, setNote] = useState('');
  const st = useAutosave((d, baseRev) => api.pfUpdate(id, { data: d, baseRev }));
  useEffect(() => {
    api.pfGet(id).then((r: any) => {
      setMeta(r); setFiles(r.files || []); setStatus(r.status || 'new');
      st.init(r.data, Number(r.data_rev || 0));
    }).catch((e: any) => st.setErr(e.message));
  }, [id]); // eslint-disable-line
  // Pending and Confirmed need every required box answered (report 2999c435). A move that would leave
  // boxes blank is not made: the boxes are listed instead, and the list shrinks as they are filled in.
  // The server checks the saved form again, so the answers on screen are saved first.
  const [tried, setTried] = useState('');
  const [stErr, setStErr] = useState('');
  const gaps = tried && st.data ? missingBoxes(st.data, t) : [];
  const changeStatus = async (v: string) => {
    setStErr('');
    if (v !== 'new' && st.data && missingBoxes(st.data, t).length) { setTried(v); return; }
    setTried('');
    const prev = status;
    setStatus(v);
    try { await st.flush(); await api.pfUpdate(id, { status: v }); }
    catch (e: any) { setStatus(prev); setStErr(e?.message || ''); }
  };
  // The client's two links. Copied, not emailed — sending them is the operator's own message.
  const copyLink = async (mode: 'edit' | 'view') => {
    try {
      const r: any = await api.pfLink(id, mode);
      if (await copyText(r.url)) setNote(t('pf_copied'));
      else window.prompt(t('pf_copy_manual'), r.url);
    } catch (e: any) { st.setErr(e.message); }
  };
  const ctl: Ctl | null = st.data ? {
    data: st.data, set: st.set, files,
    upload: async (file, path, kind) => { const r: any = await api.pfUpload(id, file, path, kind); if (r?.file) setFiles(l => l.concat([r.file])); return r; },
    remove: async fileId => { await api.pfFileDelete(id, fileId); setFiles(l => l.filter(f => f.id !== fileId)); },
    open: f => { api.pfFileOpen(id, f.id).catch(() => {}); },
    contactEmail: async name => { const r: any = await api.pfContactEmail(name); return String(r?.email || ''); },
  } : null;
  const stLabel = (s: string) => (s === 'new' ? t('pf_st_new') : s === 'pending' ? t('pf_st_pending') : t('pf_st_confirmed'));
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 892, margin: '0 auto', display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={onBack} style={{ ...btnG, display: 'inline-flex', alignItems: 'center', gap: 6 }}><i className="ti ti-arrow-left" aria-hidden />{t('pf_back')}</button>
          <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{meta?.report_no || ''}</span>
          <select value={status} onChange={e => changeStatus(e.target.value)} title={t('pf_status')} style={{ ...inp, width: 'auto' }}>
            {STATUSES.filter(Boolean).map(s => <option key={s} value={s}>{stLabel(s)}</option>)}
          </select>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{st.save === 'saving' ? t('pf_saving') : st.save === 'saved' ? t('pf_saved') : ''}</span>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => copyLink('edit')} style={{ ...btnG, display: 'inline-flex', alignItems: 'center', gap: 6 }}><i className="ti ti-link" aria-hidden />{t('pf_copy_edit')}</button>
          <button type="button" onClick={() => copyLink('view')} style={{ ...btnG, display: 'inline-flex', alignItems: 'center', gap: 6 }}><i className="ti ti-eye" aria-hidden />{t('pf_copy_view')}</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pf_link_note')}</div>
        {note && <div style={noteBox}>{note}</div>}
        {st.err && <div style={errBox}>{st.err}</div>}
        {stErr && <div style={errBox}>{stErr}</div>}
        {gaps.length > 0 && (
          <div style={errBox}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('pf_missing', { status: stLabel(tried) })}</div>
            {gaps.map((g, i) => <div key={i}>{g.block}: {g.boxes.join(', ')}</div>)}
          </div>
        )}
        {ctl ? <FormBody ctl={ctl} cur={currencyCode()} /> : !st.err && <div style={{ color: 'var(--text-tertiary)' }}>{t('pf_loading')}</div>}
      </div>
    </div>
  );
}

// ── Staff: the list ──────────────────────────────────────────────────────────────────────────────
function PFList({ onOpen, onNew }: { onOpen: (id: string) => void; onNew: () => void }) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const d: any = await api.pfList(q, status, page, limit); setRows(d.rows || []); setTotal(Number(d.total || 0)); }
    catch { /* the empty list says it */ }
    setLoading(false);
  };
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q, status, page, limit]); // eslint-disable-line
  const del = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm(t('pf_delete_confirm'))) return;
    try { await api.pfDelete(id); setRows(prev => prev.filter(r => r.id !== id)); } catch { /* */ }
  };
  const stLabel = (s: string) => (s === 'new' ? t('pf_st_new') : s === 'pending' ? t('pf_st_pending') : s === 'confirmed' ? t('pf_st_confirmed') : t('pf_all'));
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
        <div style={{ width: '100%', maxWidth: 892, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <input style={{ ...inp, flex: 1, minWidth: 120 }} placeholder={t('pf_search')} value={q} onChange={e => { setPage(1); setQ(e.target.value); }} />
            <div style={{ display: 'flex', gap: 6, flexShrink: 0, overflowX: 'auto' }}>
              {STATUSES.map(s => (
                <button key={s || 'all'} onClick={() => { setPage(1); setStatus(s); }}
                  style={{ ...btnG, whiteSpace: 'nowrap', background: status === s ? 'var(--accent)' : 'var(--bg-surface)', color: status === s ? '#fff' : 'var(--text-secondary)', borderColor: status === s ? 'var(--accent)' : 'var(--border)' }}>
                  {stLabel(s)}
                </button>
              ))}
            </div>
          </div>
          {/* ONE bubble per form, where the list was a single bordered box of rows parted by thin grey
              lines (Calem, report 19597a46, 3rd opening: "Bubble everything in the menu below (except
              Lease Registration), separate the bubbles, and give them all a green border and a green ID
              number"). Due Diligence's and Legal Matters' card exactly — white, a green edge, 10px
              corners, 10px of page between them, the palest green under the pointer (propCard,
              utils/propertyCard.ts). The form number was already the green one, and the loading and
              "no forms" lines wear the card too. Everything on the row keeps its place. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loading ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)' }}>{t('pf_loading')}</div>
              : rows.length === 0 ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('pf_none')}</div>
                : rows.map(r => (
                  <div key={r.id} onClick={() => onOpen(r.id)} style={{ ...propCard, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = PROP_CARD_HOVER)} onMouseLeave={e => (e.currentTarget.style.background = PROP_CARD_BG)}>
                    <span style={{ color: 'var(--accent)', fontWeight: 700, minWidth: 104 }}>{r.report_no}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                        {r.property_name || [r.buyer_name, r.seller_name].filter(Boolean).join(' ← ') || t('pf_untitled')}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {r.property_name && (r.buyer_name || r.seller_name) ? `${[r.buyer_name, r.seller_name].filter(Boolean).join(' ← ')} · ` : ''}
                        {r.updated_at ? `${t('pf_updated')} ${fmtDateTime(r.updated_at)}` : ''}
                        {r.updated_by_name ? ` · ${r.updated_by_name}` : ''}
                        {r.edited ? ` · ${t('pf_edited')}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)' }}>{stLabel(r.status)}</span>
                    {me?.role === 'admin' && (
                      <button title={t('pf_delete')} onClick={e => del(e, r.id)} style={{ ...btnG, padding: '4px 9px', fontSize: 14, color: '#b91c1c', flexShrink: 0 }}>
                        <i className="ti ti-trash" aria-hidden />
                      </button>
                    )}
                  </div>
                ))}
          </div>
        </div>
      </div>
      <Pagination page={page} total={total} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
      <Fab onClick={onNew} title={t('pf_new')} />
    </div>
  );
}

export default function PropertyForm({ embedded }: { embedded?: boolean }) {
  useTranslation();
  const [openId, setOpenId] = useState('');
  // A deep link opens one form straight away: /m/slf_property?v=property_form&open=<id>. Only on this
  // tab — the other tabs' `open` ids are theirs.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const v = p.get('open');
      if (v && p.get('v') === 'property_form') setOpenId(v);
    } catch { /* no query */ }
  }, []);
  const create = async () => { try { const r: any = await api.pfCreate(); setOpenId(r.id); } catch { /* */ } };
  return (
    <div className="property-form" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', height: embedded ? undefined : '100vh', fontFamily: RC }}>
      {openId
        ? <PFForm id={openId} onBack={() => setOpenId('')} />
        : <PFList onOpen={setOpenId} onNew={create} />}
    </div>
  );
}

// ── The client's links (/property-form/view/<token>, /property-form/edit/<token>) — no login ─────
// The server decides from the token which link it is; the word in the address is only for people.
export function PropertyFormPublicPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [state, setState] = useState<'load' | 'ok' | 'err'>('load');
  const [info, setInfo] = useState<any>(null);
  const [files, setFiles] = useState<any[]>([]);
  const st = useAutosave((d, baseRev) => api.pfPublicUpdate(token, { data: d, baseRev }));
  useEffect(() => {
    api.pfPublicGet(token).then((r: any) => {
      setInfo(r); setFiles(r.files || []);
      st.init(r.data, Number(r.rev || 0));
      setState('ok');
    }).catch(() => setState('err'));
  }, [token]); // eslint-disable-line
  useEffect(() => { document.title = t('pf_public_title'); }, [t]);
  const ro = info?.mode !== 'edit';
  const ctl: Ctl | null = st.data ? {
    data: st.data, set: st.set, files,
    upload: async (file, path, kind) => { const r: any = await api.pfPublicUpload(token, file, path, kind); if (r?.file) setFiles(l => l.concat([r.file])); return r; },
    remove: async fileId => { await api.pfPublicFileDelete(token, fileId); setFiles(l => l.filter(f => f.id !== fileId)); },
    open: f => { window.open(api.pfPublicFileUrl(token, f.id), '_blank'); },
  } : null;
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', fontFamily: RC, padding: 'clamp(8px, 3vw, 24px)', boxSizing: 'border-box' }}>
      <div style={{ width: '100%', maxWidth: 892, margin: '0 auto', display: 'grid', gap: 14 }}>
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pf_public_title')}</div>
          {info?.report_no && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{info.report_no}</span>}
          <div style={{ flex: 1 }} />
          {state === 'ok' && (ro
            ? <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('pf_view_only')}</span>
            : <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{st.save === 'saving' ? t('pf_saving') : st.save === 'saved' ? t('pf_saved') : ''}</span>)}
        </div>
        {state === 'load' && <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '60px 0' }}><i className="ti ti-loader" style={{ fontSize: 26 }} /></div>}
        {state === 'err' && <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '60px 0' }}><i className="ti ti-link-off" style={{ fontSize: 28, display: 'block', marginBottom: 10 }} />{t('pf_link_invalid')}</div>}
        {st.err && <div style={errBox}>{st.err}</div>}
        {state === 'ok' && ctl && <RO.Provider value={ro}><FormBody ctl={ctl} cur={String(info?.currency || '')} /></RO.Provider>}
      </div>
    </div>
  );
}
